import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { patientFormApi } from '@/services/patientFormApi';
import type { PatientFormTemplate } from '@/types/patientForm';

export default function PatientFormTemplateList() {
  const [templates, setTemplates] = useState<PatientFormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await patientFormApi.getTemplates();
      // Handle both array and paginated response
      const templates = Array.isArray(data) ? data : ((data as any).results || []);
      setTemplates(templates);
    } catch (err: any) {
      console.error('Error loading templates:', err);
      setError(err.response?.data?.message || 'Failed to load templates');
      toast.error('Failed to load form templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      await patientFormApi.setDefaultTemplate(id);
      toast.success('Default template updated');
      loadTemplates();
    } catch (err: any) {
      console.error('Error setting default:', err);
      toast.error(err.response?.data?.message || 'Failed to set default template');
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Are you sure you want to delete template "${name}"?`)) {
      return;
    }

    try {
      await patientFormApi.deleteTemplate(id);
      toast.success('Template deleted successfully');
      loadTemplates();
    } catch (err: any) {
      console.error('Error deleting template:', err);
      toast.error(err.response?.data?.message || 'Failed to delete template');
    }
  };

  const handleToggleActive = async (template: PatientFormTemplate) => {
    try {
      await patientFormApi.patchTemplate(template.id, {
        is_active: !template.is_active,
      });
      toast.success(`Template ${template.is_active ? 'deactivated' : 'activated'}`);
      loadTemplates();
    } catch (err: any) {
      console.error('Error toggling active:', err);
      toast.error(err.response?.data?.message || 'Failed to update template');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadTemplates}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Biểu Mẫu Thông Tin Bệnh Nhân</h1>
          <p className="text-sm text-gray-600 mt-1">
            Quản lý các biểu mẫu thông tin bệnh nhân
          </p>
        </div>
        <Link
          to="/pathology/patients/form-builder/editor/new"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          + Create Template
        </Link>
      </div>

      {/* Templates list */}
      {templates.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No templates</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new template.</p>
          <div className="mt-6">
            <Link
              to="/pathology/patients/form-builder/editor/new"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              + Create Template
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {template.name}
                    </h3>
                    {template.is_default && (
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        Default
                      </span>
                    )}
                    {!template.is_active && (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                        Inactive
                      </span>
                    )}
                    {template.organization_name && (
                      <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                        {template.organization_name}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                    <span>
                      {template.form_config.sections.length} sections
                    </span>
                    <span>•</span>
                    <span>
                      {template.form_config.sections.reduce(
                        (sum, section) => sum + section.fields.length,
                        0
                      )}{' '}
                      fields
                    </span>
                    <span>•</span>
                    <span>Version {template.version}</span>
                    <span>•</span>
                    <span>
                      Updated {new Date(template.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  <Link
                    to={`/pathology/patients/form-builder/${template.id}`}
                    className="px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded transition-colors"
                  >
                    Preview
                  </Link>
                  <Link
                    to={`/pathology/patients/form-builder/editor/${template.id}`}
                    className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    Edit
                  </Link>
                  {!template.is_default && (
                    <button
                      onClick={() => handleSetDefault(template.id)}
                      className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    >
                      Set as Default
                    </button>
                  )}
                  <button
                    onClick={() => handleToggleActive(template)}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  >
                    {template.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleDelete(template.id, template.name)}
                    className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
